import React from 'react';
import { Home, Compass, MessageSquare, User, Flame, Settings, HelpCircle, Bookmark, Bell, LogOut } from 'lucide-react';

interface NavItemProps {
  icon: React.ReactNode;
  label: string;
  isActive: boolean;
  onClick: () => void;
  keyShortcut?: string;
}

function NavItem({ icon, label, isActive, onClick, keyShortcut }: NavItemProps) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center w-full px-4 py-3 rounded-lg transition-colors ${
        isActive 
          ? 'bg-blue-600/10 text-blue-500' 
          : 'text-gray-400 hover:bg-gray-800/50 hover:text-gray-200'
      }`}
    >
      <div className={`mr-3 ${isActive ? 'text-blue-500' : ''}`}>
        {icon}
      </div>
      <span className="font-medium">{label}</span>
      {keyShortcut && (
        <span className="ml-auto bg-gray-800 text-gray-400 text-xs px-1.5 py-0.5 rounded">
          {keyShortcut}
        </span>
      )}
    </button>
  );
}

interface SideNavProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
}

export function SideNav({ activeTab, onTabChange }: SideNavProps) {
  return (
    <div className="fixed left-0 top-0 bottom-0 w-[240px] bg-black/95 border-r border-gray-800 z-30 pt-16 flex flex-col">
      <div className="p-3 flex-1 overflow-y-auto custom-scrollbar">
        <div className="mb-6">
          <h3 className="text-gray-500 uppercase text-xs font-semibold px-4 mb-2">Main</h3>
          <NavItem 
            icon={<Home className="w-5 h-5" />} 
            label="Home"
            isActive={activeTab === 'home'}
            onClick={() => onTabChange('home')}
            keyShortcut="1"
          />
          <NavItem 
            icon={<Compass className="w-5 h-5" />} 
            label="Discover"
            isActive={activeTab === 'discover'}
            onClick={() => onTabChange('discover')}
            keyShortcut="2"
          />
          <NavItem 
            icon={<Flame className="w-5 h-5" />} 
            label="Trending"
            isActive={activeTab === 'trending'}
            onClick={() => onTabChange('trending')}
            keyShortcut="3"
          />
        </div>
        
        <div className="mb-6">
          <h3 className="text-gray-500 uppercase text-xs font-semibold px-4 mb-2">Social</h3>
          <NavItem 
            icon={<MessageSquare className="w-5 h-5" />} 
            label="Messages"
            isActive={activeTab === 'chat'}
            onClick={() => onTabChange('chat')}
            keyShortcut="4"
          />
          <NavItem 
            icon={<Bell className="w-5 h-5" />} 
            label="Notifications"
            isActive={activeTab === 'notifications'}
            onClick={() => {}}
          />
          <NavItem 
            icon={<Bookmark className="w-5 h-5" />} 
            label="Saved"
            isActive={activeTab === 'saved'}
            onClick={() => {}}
          />
        </div>
        
        <div className="mb-6">
          <h3 className="text-gray-500 uppercase text-xs font-semibold px-4 mb-2">Account</h3>
          <NavItem 
            icon={<User className="w-5 h-5" />} 
            label="Profile"
            isActive={activeTab === 'profile'}
            onClick={() => onTabChange('profile')}
            keyShortcut="5"
          />
          <NavItem 
            icon={<Settings className="w-5 h-5" />} 
            label="Settings"
            isActive={activeTab === 'settings'}
            onClick={() => {}}
          />
        </div>
        
        <div className="mb-6">
          <h3 className="text-gray-500 uppercase text-xs font-semibold px-4 mb-2">Support</h3>
          <NavItem 
            icon={<HelpCircle className="w-5 h-5" />} 
            label="Help & FAQ"
            isActive={activeTab === 'help'}
            onClick={() => {}}
          />
        </div>
      </div>
      
      <div className="p-3 border-t border-gray-800">
        <button 
          className="flex items-center w-full px-4 py-3 rounded-lg text-red-400 hover:bg-red-600/10 transition-colors"
        >
          <LogOut className="w-5 h-5 mr-3" />
          <span className="font-medium">Logout</span>
        </button>
      </div>
    </div>
  );
}